import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { positionAPI, profileAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { getJobLocation } from "../utils/jobDisplay";
import "./Jobs.scss";

/** Strip HTML tags and normalize whitespace for plain-text preview */
function stripHtml(html) {
  if (!html || typeof html !== "string") return "";
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return text;
}

function Jobs() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState(() => ({
    search: searchParams.get("search") || "",
    techStack: "",
    employmentType: "",
    experienceLevel: "",
    location: searchParams.get("location") || "",
  }));
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0,
  });
  const [filterOptions, setFilterOptions] = useState({
    techStacks: [],
    locations: [],
    employmentTypes: [],
    experienceLevels: [],
  });
  const [appliedPositionIds, setAppliedPositionIds] = useState(new Set());

  const observer = useRef();

  useEffect(() => {
    if (!isAuthenticated) {
      setAppliedPositionIds(new Set());
      return;
    }
    profileAPI
      .getApplications()
      .then((res) => {
        if (res.data?.success && res.data?.data?.applications) {
          const ids = new Set(
            res.data.data.applications.map((app) => {
              const pos = app.position && typeof app.position === "object" ? app.position : null;
              const id = pos?._id ?? (typeof app.positionId === "object" ? app.positionId?._id : app.positionId);
              return id ? String(id) : null;
            }).filter(Boolean)
          );
          setAppliedPositionIds(ids);
        }
      })
      .catch(() => setAppliedPositionIds(new Set()));
  }, [isAuthenticated]);
  const lastJobElementRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();
      
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreJobs();
        }
      });
      
      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore, loading]
  );

  useEffect(() => {
    document.title = "Careers - Talsolution";
    return () => { document.title = "Talsolution"; };
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Sync URL query params into filters (e.g. when arriving from home search or browser back)
  useEffect(() => {
    const search = searchParams.get("search") || "";
    const location = searchParams.get("location") || "";
    setFilters(prev => {
      if (prev.search === search && prev.location === location) return prev;
      return { ...prev, search, location };
    });
  }, [searchParams]);

  useEffect(() => {
    // Reset jobs when filters change
    setJobs([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    setHasMore(true);
    fetchJobs(1, true);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await positionAPI.getFilters();
      if (response.data.success) {
        const data = response.data.data;
        setFilterOptions({
          ...data,
          experienceLevels: ['Junior', 'Mid', 'Senior', 'Lead', 'Any'],
        });
        setFilters((prev) =>
          prev.experienceLevel === 'Entry' ? { ...prev, experienceLevel: 'Junior' } : prev
        );
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchJobs = async (page = 1, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    
    try {
      const params = {
        ...filters,
        page: page,
        limit: pagination.limit,
      };
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await positionAPI.getAll(params);
      
      if (response.data.success) {
        const newJobs = response.data.data;
        const paginationData = response.data.pagination;
        
        if (reset) {
          setJobs(newJobs);
        } else {
          setJobs(prev => [...prev, ...newJobs]);
        }
        
        setPagination({
          page: paginationData.page,
          limit: paginationData.limit,
          total: paginationData.total,
          pages: paginationData.pages,
        });
        
        setHasMore(paginationData.page < paginationData.pages);
      }
    } catch (err) {
      setError("Failed to load jobs. Please try again later.");
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreJobs = () => {
    if (!loadingMore && hasMore) {
      const nextPage = pagination.page + 1;
      fetchJobs(nextPage, false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      techStack: "",
      employmentType: "",
      experienceLevel: "",
      location: "",
    });
  };

  const calculateDaysAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    // Compare calendar days in local time so "Today" means "posted on this day"
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
    const days = Math.round((nowDay - thenDay) / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  };

  const calculateDaysLeft = (deadline) => {
    if (!deadline) return null;
    const days = Math.floor((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return "Expired";
    if (days === 0) return "Last day";
    return `${days} days left`;
  };

  return (
    <>
      <Header />

      <section className="ts-page-hero">
        <div className="tf-container">
          <span className="ts-eyebrow ts-eyebrow--dark">Careers at TalSolution</span>
          <h1 className="ts-page-hero__title">Find your next US role</h1>
          <p className="ts-page-hero__lead">
            Vetted contract and full-time positions with US companies. Applying is always free.
          </p>
        </div>
      </section>
      <section className="jobs-page-section">
        <div className="tf-container">
          {/* Horizontal Filter Bar */}
          <div className="jobs-filter-bar">
            <div className="filter-row">
              <div className="filter-item search-filter">
                <input
                  type="text"
                  placeholder="Search by job title, skills..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </div>

              <div className="filter-item">
                <select
                  value={filters.employmentType}
                  onChange={(e) => handleFilterChange("employmentType", e.target.value)}
                >
                  <option value="">Employment Type</option>
                  {filterOptions.employmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <select
                  value={filters.experienceLevel}
                  onChange={(e) => handleFilterChange("experienceLevel", e.target.value)}
                >
                  <option value="">Experience Level</option>
                  {filterOptions.experienceLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <input
                  type="text"
                  placeholder="Location"
                  value={filters.location}
                  onChange={(e) => handleFilterChange("location", e.target.value)}
                />
              </div>

              <button className="clear-filters-btn" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>

            <div className="results-info">
              <p><strong>{pagination.total}</strong> {pagination.total === 1 ? "open position" : "open positions"}</p>
            </div>
          </div>

          {/* Jobs Grid */}
          {loading && jobs.length === 0 ? (
            <div className="jobs-grid">
              {[...Array(12)].map((_, index) => (
                <div key={index} className="job-card-skeleton">
                  <div className="skeleton-header">
                    <div className="skeleton-logo skeleton-animate"></div>
                    <div className="skeleton-badges">
                      <div className="skeleton-badge skeleton-animate"></div>
                      <div className="skeleton-badge skeleton-animate"></div>
                    </div>
                  </div>
                  <div className="skeleton-body">
                    <div className="skeleton-title skeleton-animate"></div>
                    <div className="skeleton-company skeleton-animate"></div>
                    <div className="skeleton-info-row">
                      <div className="skeleton-info skeleton-animate"></div>
                      <div className="skeleton-info skeleton-animate"></div>
                      <div className="skeleton-info skeleton-animate"></div>
                    </div>
                    <div className="skeleton-tags">
                      <div className="skeleton-tag skeleton-animate"></div>
                      <div className="skeleton-tag skeleton-animate"></div>
                    </div>
                    <div className="skeleton-description skeleton-animate"></div>
                    <div className="skeleton-description skeleton-animate"></div>
                  </div>
                  <div className="skeleton-footer">
                    <div className="skeleton-salary skeleton-animate"></div>
                    <div className="skeleton-button skeleton-animate"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="empty-state">
              <h3>No positions found</h3>
              <p>Try adjusting your filters or check back later for new opportunities.</p>
              <button onClick={clearFilters} className="tf-btn">
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="jobs-grid">
                {jobs.map((job, index) => {
                  const isLastElement = jobs.length === index + 1;
                  const isApplied = appliedPositionIds.has(String(job._id));
                  return (
                    <div
                      key={job._id}
                      className={`job-card ${isApplied ? "job-card-applied" : ""}`}
                      ref={isLastElement ? lastJobElementRef : null}
                      onClick={() => navigate(`/careers/${job._id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="job-card-header">
                        <div className="company-logo">
                          {(job.title && job.title.trim().length > 0)
                            ? job.title.trim().charAt(0).toUpperCase()
                            : (job.company && job.company.charAt(0)) || "?"}
                        </div>
                        <div className="job-meta-badges">
                          {isApplied && (
                            <span className="badge badge-applied">
                              <i className="icon-check"></i> Applied
                            </span>
                          )}
                          {job.priority === "Urgent" && (
                            <span className="badge badge-urgent">
                              <i className="icon-bolt"></i> Urgent
                            </span>
                          )}
                          {Array.isArray(job.workType) &&
                            job.workType.length > 0 &&
                            job.workType.map((wt) => (
                              <span
                                key={wt}
                                className={`badge badge-${String(wt).toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                {wt}
                              </span>
                            ))}
                        </div>
                      </div>

                      <div className="job-card-body">
                        <h3 className="job-title">{job.title}</h3>
                        <h4 className="company-name">{job.company}</h4>

                        <div className="job-info-row">
                          {getJobLocation(job) && (
                            <span className="info-item">
                              <i className="icon-map-pin"></i>
                              {getJobLocation(job)}
                            </span>
                          )}
                        </div>

                        <div className="job-tags">
                          {job.department && (
                            <span className="tag">{job.department}</span>
                          )}
                          {job.employmentType && (
                            <span className="tag">{job.employmentType}</span>
                          )}
                          {job.experienceLevel && (
                            <span className="tag">{job.experienceLevel}</span>
                          )}
                          <span className="tag tag-date">{calculateDaysAgo(job.created)}</span>
                          {job.techStack && (
                            <span className="tag">{job.techStack}</span>
                          )}
                        </div>

                        {job.description && (
                          <p className="job-description">
                            {stripHtml(job.description).substring(0, 320)}
                            {stripHtml(job.description).length > 320 ? "…" : ""}
                          </p>
                        )}
                      </div>

                      <div className="job-card-footer">
                        <div className="footer-right">
                          {job.deadline && (
                            <span className="deadline">{calculateDaysLeft(job.deadline)}</span>
                          )}
                          <button
                            className={isApplied ? "apply-btn apply-btn-view" : "apply-btn"}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/careers/${job._id}`);
                            }}
                          >
                            {isApplied ? (
                              <>View <i className="icon-arrow-right2"></i></>
                            ) : (
                              <>View role <i className="icon-arrow-right2"></i></>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Loading More Indicator */}
              {loadingMore && (
                <div className="jobs-grid">
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="job-card-skeleton">
                      <div className="skeleton-header">
                        <div className="skeleton-logo skeleton-animate"></div>
                        <div className="skeleton-badges">
                          <div className="skeleton-badge skeleton-animate"></div>
                          <div className="skeleton-badge skeleton-animate"></div>
                        </div>
                      </div>
                      <div className="skeleton-body">
                        <div className="skeleton-title skeleton-animate"></div>
                        <div className="skeleton-company skeleton-animate"></div>
                        <div className="skeleton-info-row">
                          <div className="skeleton-info skeleton-animate"></div>
                          <div className="skeleton-info skeleton-animate"></div>
                          <div className="skeleton-info skeleton-animate"></div>
                        </div>
                        <div className="skeleton-tags">
                          <div className="skeleton-tag skeleton-animate"></div>
                          <div className="skeleton-tag skeleton-animate"></div>
                        </div>
                        <div className="skeleton-description skeleton-animate"></div>
                        <div className="skeleton-description skeleton-animate"></div>
                      </div>
                      <div className="skeleton-footer">
                        <div className="skeleton-salary skeleton-animate"></div>
                        <div className="skeleton-button skeleton-animate"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
      <Gotop />
    </>
  );
}

export default Jobs;
